import { Injectable } from '@angular/core';
import API, { graphqlOperation } from '@aws-amplify/api';
import { KeyStringFilterInput, StringFilterInput, BooleanFilterInput } from 'app/api/common.api.types';
import { EMPTY, from, Observable } from 'rxjs';
import { concatAll, expand, map, toArray } from 'rxjs/operators';
import { CreateFileUploadEntryInput, UpdateFileUploadEntryInput } from './API.service';

export interface FileUploadFilterInput {
  s3SignedURL: StringFilterInput | null;
  sourceFilename: StringFilterInput | null;
  fileType: StringFilterInput | null;
  uploadDate: StringFilterInput | null;
  startDate: StringFilterInput | null;
  untilDateExcluded: StringFilterInput | null;
  closedAccounting: BooleanFilterInput | null;
}

export interface ImportResult {
  status: string;
  rows: number | null;
  ignoredRows: number | null;
  message: string | null;
}

export interface FileUploadEntry {
  companyId: string;
  key: string;
  s3SignedURL: string | null;
  sourceFilename: string;
  fileType: string;
  uploadDate: string;
  startDate: string | null;
  untilDateExcluded: string | null;
  closedAccounting: boolean | null;
  accountingSheet: ImportResult | null;
  balanceSheet: ImportResult | null;
  payrollSheet: ImportResult | null;
  planSheet: ImportResult | null;
  analyticsSheet: ImportResult | null;
  customerSheet: ImportResult | null;
  providerSheet: ImportResult | null;
  journalSheet: ImportResult | null;
  importResult: ImportResult | null;
}

export interface FileUploadConnection {
  items: FileUploadEntry[];
  lastEvaluatedKey: any;
}

@Injectable({
  providedIn: 'root'
})
export class FileUploadAPIService {
  
  CreateFileUploadEntry(
    input: CreateFileUploadEntryInput
  ): Observable<FileUploadEntry> {
    const statement = `mutation CreateFileUploadEntry($input: CreateFileUploadEntryInput!) {
      createFileUploadEntry(input: $input) {
        companyId
        key
        s3SignedURL
        sourceFilename
        fileType
        uploadDate
        startDate
        untilDateExcluded
        closedAccounting
        accountingSheet {
          status
          rows
          ignoredRows
          message
        }
        balanceSheet {
          status
          rows
          message
        }
        payrollSheet {
          status
          rows
          message
        }
        planSheet {
          status
          rows
          message
        }
        analyticsSheet {
          status
          rows
          message
        }
        customerSheet {
          status
          rows
          message
        }
        providerSheet {
          status
          rows
          message
        }
        journalSheet {
          status
          rows
          message
        }
        importResult {
          status
          rows
          message
        }
      }
    }`;
    const gqlAPIServiceArguments: any = {
      input
    };
    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
      )).pipe(
        map((response: any) => {
          return response.data.createFileUploadEntry as FileUploadEntry;
        })
      );
  }

  UpdateFileUploadEntry(
    input: UpdateFileUploadEntryInput
  ): Observable<FileUploadEntry> {
    const statement = `mutation UpdateFileUploadEntry($input: UpdateFileUploadEntryInput!) {
      updateFileUploadEntry(input: $input) {
        companyId
        key
        s3SignedURL
        sourceFilename
        fileType
        uploadDate
        startDate
        untilDateExcluded
        closedAccounting
        accountingSheet {
          status
          rows
          ignoredRows
          message
        }
        balanceSheet {
          status
          rows
          message
        }
        payrollSheet {
          status
          rows
          message
        }
        planSheet {
          status
          rows
          message
        }
        analyticsSheet {
          status
          rows
          message
        }
        customerSheet {
          status
          rows
          message
        }
        providerSheet {
          status
          rows
          message
        }
        journalSheet {
          status
          rows
          message
        }
        importResult {
          status
          rows
          message
        }
      }
    }`;
    const gqlAPIServiceArguments: any = {
      input
    };

    return from(API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
      )).pipe(
        map((response: any) => {
          return response.data.updateFileUploadEntry as FileUploadEntry;
        })
      );
  }

  FileUpload(
    companyId: string, 
    key?: KeyStringFilterInput,
    filter?: FileUploadFilterInput,
    limit?: number,
  ): Observable<FileUploadEntry[]> {
    // tslint:disable-next-line: max-line-length
    const statement = `query FileUpload($id: String!, $key: KeyStringFilterInput, $filter: FileUploadFilterInput, $limit: Int, $exclusiveStartKey: AWSJSON) {
      company(id: $id) {
        fileUpload(key: $key, filter: $filter, limit: $limit, exclusiveStartKey: $exclusiveStartKey) {
          items {
            companyId
            key
            s3SignedURL
            sourceFilename
            fileType
            uploadDate
            startDate
            untilDateExcluded
            closedAccounting
            accountingSheet {
              status
              rows
              ignoredRows
              message
            }
            balanceSheet {
              status
              rows
              message
            }
            payrollSheet {
              status
              rows
              message
            }
            planSheet {
              status
              rows
              message
            }
            analyticsSheet {
              status
              rows
              message
            }
            customerSheet {
              status
              rows
              message
            }
            providerSheet {
              status
              rows
              message
            }
            journalSheet {
              status
              rows
              message
            }
            importResult {
              status
              rows
              message
            }
          }
          lastEvaluatedKey
        }
      }
    }`;
    const gqlAPIServiceArguments: any = {};
    gqlAPIServiceArguments.id = companyId;
    if (key) {
      gqlAPIServiceArguments.key = key;
    }
    if (filter) {
      gqlAPIServiceArguments.filter = filter;
    }
    if (limit) {
      gqlAPIServiceArguments.limit = limit;
    }

    return this.performQuery(statement, gqlAPIServiceArguments)
    .pipe(
      expand(conn => {
        const exclusiveStartKey = conn.lastEvaluatedKey;
        const args = { ...gqlAPIServiceArguments, exclusiveStartKey };
        return (conn.lastEvaluatedKey && JSON.parse(conn.lastEvaluatedKey) !== '')
          ? this.performQuery(statement, args)
          : EMPTY;
        }),
      map(conn => conn.items),
      concatAll(),
      toArray()
    );
  }

  private performQuery(statement: string, args: any = {}): Observable<FileUploadConnection> {
    return from(API.graphql(
      graphqlOperation(statement, args)
      )).pipe(
        map((response: any) => {
          return response.data.company.fileUpload as FileUploadConnection;
        })
      );
  }

}
